import React, { useState, useMemo, useEffect } from "react"
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Input, Label, FormGroup } from "reactstrap"
import { useMutation, gql, useQuery } from "@apollo/client"
import Select from "react-select"
import "./CommsNewConversationModal.css"
import { GET_ALL_CHANNELS } from "./Queries"
import { MessagingSendMessageMutation } from "./Mutations"
import { DriverTypeAhead } from "../TaskManagement/TaskComponents/TypeAheadComponents"
import MessageComposer from "./SendMessage/MessageComposer"
import { useNavigate } from "react-router-dom"
import { GetConfigurationSettings } from "../Functions/querys"

const UPSERT_CONVERSATION_MESSAGE = gql`
  mutation MessagingUpsertConversationMessage(
    $to: String
    $driverId: ID
    $body: String!
    $bodyHtml: String
    $channelId: ID
    $subject: String
    $conversationId: ID
  ) {
    messagingUpsertConversationMessage(
      to: $to
      driverId: $driverId
      body: $body
      bodyHtml: $bodyHtml
      channelId: $channelId
      subject: $subject
      conversationId: $conversationId
    ) {
      ok
      errors
      conversation {
        id
      }
      message {
        id
      }
    }
  }
`

const emptyForm = {
  to: "",
  driverId: "",
  subject: "",
  channelId: "",
}

export default function CommsNewConversationModal({ isOpen, toggle, defaultDriverId, onCreated }) {
  const navigate = useNavigate()
  const [formData, setFormData] = useState(() => defaultDriverId ? { ...emptyForm, driverId: defaultDriverId } : emptyForm)
  const [recipientType, setRecipientType] = useState("driver")
  const [error, setError] = useState("")

  const [sendMutation] = useMutation(UPSERT_CONVERSATION_MESSAGE)
  const [sendFilesMutation] = useMutation(MessagingSendMessageMutation)

  const { data: channelsData, loading: loadingChannels } = useQuery(GET_ALL_CHANNELS, {
    variables: { first: 100 },
    fetchPolicy: "cache-and-network",
  })
  const channels = channelsData?.allChannels?.edges.map(edge => edge.node) || []

  const channelOptions = useMemo(
    () => channels.map(c => ({ value: c.id, label: `${c.name} (${c.channelType})`, channelType: c.channelType, sendAs: c.sendAs })),
    [channels]
  )
  const selectedChannelOption = channelOptions.find(opt => opt.value === formData.channelId) || null

  const isEmailChannel = selectedChannelOption?.channelType === "EMAIL"

  // Resolves the driver's branch default SMS number, so we can pre-select that branch's channel.
  const [branchDefaultNumber, setBranchDefaultNumber] = useState(null)

  useEffect(() => {
    setBranchDefaultNumber(null)
  }, [recipientType, formData.driverId])

  useQuery(GetConfigurationSettings, {
    skip: recipientType !== "driver" || !formData.driverId,
    variables: { driverId: formData.driverId, name: "SMS_FROM_DEFAULT" },
    fetchPolicy: "cache-first",
    onCompleted: (data) => setBranchDefaultNumber(data?.getConfigurationSettings || ""),
  })

  const resolvedBranchChannel = useMemo(
    () => (branchDefaultNumber ? channelOptions.find(opt => opt.sendAs === branchDefaultNumber) || null : null),
    [branchDefaultNumber, channelOptions]
  )

  useEffect(() => {
    if (formData.channelId || !resolvedBranchChannel) return
    setFormData(prev => ({ ...prev, channelId: resolvedBranchChannel.value }))
  }, [resolvedBranchChannel, formData.channelId])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleChannelChange = (option) => {
    setFormData(prev => ({ ...prev, channelId: option?.value || "" }))
  }

  const handleRecipientTypeToggle = (type) => {
    setRecipientType(type)
    setFormData(prev => ({ ...prev, driverId: "", to: "" }))
  }

  // Called by MessageComposer when the user hits Send
  const handleSend = async (body, bodyHtml, files) => {
    if (!formData.channelId) throw new Error("Please select a channel")
    if (!formData.driverId && !formData.to) throw new Error("Please select a recipient")

    const { data } = await sendMutation({
      variables: {
        to: formData.to || null,
        driverId: formData.driverId || null,
        body: body || (files.length > 0 ? "📎 Attachment" : ""),
        bodyHtml: bodyHtml || null,
        subject: formData.subject || null,
        channelId: formData.channelId,
      },
    })

    if (!data?.messagingUpsertConversationMessage?.ok) {
      throw new Error(data?.messagingUpsertConversationMessage?.errors?.toString() || "An unknown error occurred")
    }

    const newConversationId = data.messagingUpsertConversationMessage.conversation?.id

    if (files.length > 0 && newConversationId) {
      await sendFilesMutation({
        variables: { conversationId: newConversationId, body: "📎 Attachment", files },
      })
    }

    toggle()
    setFormData(emptyForm)
    setRecipientType("driver")
    setError("")

    if (onCreated) {
      onCreated(newConversationId)
    } else {
      navigate(`/comms/conversation/${newConversationId}/`)
    }
  }

  return (
    <Modal isOpen={isOpen} centered size="lg" className="CommsNewConversationModal">
      <ModalHeader>Start a New Conversation</ModalHeader>
      <ModalBody>
        {error && <div className="alert alert-danger">Error: {error}</div>}

        <FormGroup>
          <Label for="channelId">Channel</Label>
          <Select
            inputId="channelId"
            options={channelOptions}
            value={selectedChannelOption}
            onChange={handleChannelChange}
            isLoading={loadingChannels}
            placeholder="Search channels..."
            isClearable
            classNamePrefix="channel-select"
          />
        </FormGroup>

        <FormGroup>
          <Label>Recipient</Label>
          <div className="recipient-type-toggle">
            <Button
              size="sm"
              color={recipientType === "driver" ? "primary" : "light"}
              onClick={() => handleRecipientTypeToggle("driver")}
            >
              Driver
            </Button>
            <Button
              size="sm"
              color={recipientType === "manual" ? "primary" : "light"}
              onClick={() => handleRecipientTypeToggle("manual")}
            >
              Contact (manual)
            </Button>
          </div>
          {recipientType === "driver" ? (
            <DriverTypeAhead
              key="driver-typeahead"
              defaultValue={defaultDriverId || null}
              onChange={(driverId) => setFormData(prev => ({ ...prev, driverId, to: "" }))}
            />
          ) : (
            <Input
              type="text"
              name="to"
              value={formData.to}
              onChange={handleChange}
              placeholder="Email or phone number"
            />
          )}
        </FormGroup>

        {isEmailChannel && (
          <FormGroup>
            <Label for="subject">Subject</Label>
            <Input
              type="text"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
            />
          </FormGroup>
        )}

        <div className="new-conversation-composer">
          <MessageComposer
            channelType={selectedChannelOption?.channelType}
            onSend={handleSend}
            hideNoteComposer
          />
        </div>
      </ModalBody>

      <ModalFooter>
        <Button color="secondary" onClick={toggle}>Cancel</Button>
      </ModalFooter>
    </Modal>
  )
}
